import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../contexts/AuthContext";

// Thin axios wrapper bound to the Firebase auth header (token auto-refreshed by
// the global interceptor in AuthContext). No react-query — matches the codebase.
export function useFb() {
  const { getAuthHeader } = useAuth();
  return {
    get: (path, params) => axios.get(`${API}${path}`, { headers: getAuthHeader(), params }).then((r) => r.data),
    post: (path, body) => axios.post(`${API}${path}`, body || {}, { headers: getAuthHeader() }).then((r) => r.data),
    put: (path, body) => axios.put(`${API}${path}`, body || {}, { headers: getAuthHeader() }).then((r) => r.data),
    del: (path, params) => axios.delete(`${API}${path}`, { headers: getAuthHeader(), params }).then((r) => r.data),
  };
}

export function shortName(full) {
  if (!full) return "Player";
  const p = full.trim().split(/\s+/);
  return p.length === 1 ? full : `${p[0][0]}. ${p[p.length - 1]}`;
}
export function lastName(full) {
  if (!full) return "Player";
  return full.trim().split(/\s+/).slice(-1)[0];
}

export const POSITION_RULES = {
  GK: { min: 1, max: 1 },
  DEF: { min: 3, max: 5 },
  MID: { min: 3, max: 5 },
  FWD: { min: 1, max: 3 },
};
export const BUDGET = 100;

// ---- Formations (Issue 3) ----
// Explicit formation shapes. The pitch shows exactly (shape[pos] - picked[pos])
// empty slots per position, so removing a player keeps an empty slot at THAT
// position (no auto-rebalancing). Formation only changes when the user picks one.
export const FORMATIONS = {
  "4-4-2": { GK: 1, DEF: 4, MID: 4, FWD: 2 },
  "4-3-3": { GK: 1, DEF: 4, MID: 3, FWD: 3 },
  "3-4-3": { GK: 1, DEF: 3, MID: 4, FWD: 3 },
  "3-5-2": { GK: 1, DEF: 3, MID: 5, FWD: 2 },
  "5-3-2": { GK: 1, DEF: 5, MID: 3, FWD: 2 },
  "5-4-1": { GK: 1, DEF: 5, MID: 4, FWD: 1 },
};
export const FORMATION_LIST = ["4-4-2", "4-3-3", "3-4-3", "3-5-2", "5-3-2", "5-4-1"];
export const DEFAULT_FORMATION = "4-4-2";
export const POSITION_WORD = { GK: "goalkeeper", DEF: "defender", MID: "midfielder", FWD: "forward" };

// Empty slots for a FIXED formation — no growing/shrinking. Each position simply
// shows the slots the formation needs that aren't filled yet.
export function emptySlotsForFormation(squad, formation) {
  const shape = FORMATIONS[formation] || FORMATIONS[DEFAULT_FORMATION];
  const counts = positionCounts(squad);
  const empty = {};
  ["GK", "DEF", "MID", "FWD"].forEach((p) => { empty[p] = Math.max(0, shape[p] - (counts[p] || 0)); });
  return empty;
}

// Infer a formation from an existing squad's counts (for legacy squads with no
// stored formation). Falls back to 4-4-2 if the shape isn't one we offer.
export function deriveFormation(squad) {
  const c = positionCounts(squad);
  const key = `${c.DEF || 0}-${c.MID || 0}-${c.FWD || 0}`;
  return FORMATION_LIST.includes(key) ? key : DEFAULT_FORMATION;
}

export function positionCounts(squad) {
  const c = { GK: 0, DEF: 0, MID: 0, FWD: 0 };
  squad.filter((p) => !p.is_super_sub).forEach((p) => { if (c[p.position] != null) c[p.position]++; });
  return c;
}

// Formation-aware empty-slot allocation. Renders exactly enough empty chips so
// picked + empty === 11, distributed so users can build 3-5-2 / 4-3-3 / 5-3-2 /
// 5-4-1 / 3-4-3 / 4-4-2 etc. (not hardcoded to 4-4-2).
export function computeEmptySlots(squad) {
  const counts = positionCounts(squad);
  const order = ["GK", "DEF", "MID", "FWD"];
  const slots = {};
  order.forEach((p) => { slots[p] = Math.max(POSITION_RULES[p].min, counts[p] || 0); });
  let total = order.reduce((a, p) => a + slots[p], 0);
  // Grow to 11 — breadth-first across positions that still have room.
  const growOrder = ["DEF", "MID", "FWD", "GK"];
  let guard = 0;
  while (total < 11 && guard++ < 60) {
    let added = false;
    for (const p of growOrder) {
      if (total >= 11) break;
      if (slots[p] < POSITION_RULES[p].max) { slots[p]++; total++; added = true; }
    }
    if (!added) break;
  }
  // Shrink to 11 — remove empty (un-picked) slots from highest-pick positions first.
  guard = 0;
  while (total > 11 && guard++ < 60) {
    const cands = order
      .filter((p) => slots[p] > (counts[p] || 0) && slots[p] > POSITION_RULES[p].min)
      .sort((a, b) => (counts[b] || 0) - (counts[a] || 0));
    if (!cands.length) break;
    slots[cands[0]]--; total--;
  }
  const empty = {};
  order.forEach((p) => { empty[p] = Math.max(0, slots[p] - (counts[p] || 0)); });
  return empty;
}

export function budgetSpent(squad) {
  return squad.filter((p) => !p.is_super_sub).reduce((s, p) => s + (p.credits || 0), 0);
}

export function validateSquad(squad) {
  const errors = [];
  const main = squad.filter((p) => !p.is_super_sub);
  if (main.length !== 11) errors.push(`Pick ${11 - main.length} more player${11 - main.length !== 1 ? "s" : ""}`);
  const c = positionCounts(squad);
  Object.entries(POSITION_RULES).forEach(([pos, { min, max }]) => {
    if (c[pos] < min || c[pos] > max) errors.push(`${pos}: need ${min}-${max} (have ${c[pos]})`);
  });
  if (budgetSpent(squad) > BUDGET) errors.push("Over budget");
  if (!main.find((p) => p.is_captain)) errors.push("Set a captain");
  if (!main.find((p) => p.is_vice_captain)) errors.push("Set a vice-captain");
  return errors;
}

export function isReadyToLock(squad) {
  return validateSquad(squad).length === 0;
}

// Live countdown to an ISO timestamp. Returns { label, passed }.
export function useCountdown(targetIso) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!targetIso) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [targetIso]);
  if (!targetIso) return { label: "—", passed: false };
  const diff = new Date(targetIso).getTime() - now;
  if (diff <= 0) return { label: "Locked", passed: true };
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  const label = d > 0 ? `${d}d ${h}h ${m}m` : h > 0 ? `${h}h ${m}m ${s}s` : `${m}m ${s}s`;
  return { label, passed: false };
}

export function formatMatchDay(md) {
  if (!md) return "";
  try {
    const d = new Date(md + "T12:00:00");
    return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
  } catch {
    return md;
  }
}

// Count-up animation hook (0 -> target). Used on results total score.
export function useCountUp(target, durationMs = 1500) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const ease = (p) => 1 - Math.pow(1 - p, 3);
    const tick = (t) => {
      const p = Math.min(1, (t - start) / durationMs);
      setVal(target * ease(p));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);
  return val;
}
